import React, { ComponentType, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Route, Switch, useHistory, useParams } from 'react-router-dom';
import { ProductType } from '@tink/core/src/types';
import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { ConsentDirector } from '../../../connectivity/screens/consent/ConsentDirector';
import { applicationParameters } from '../../../utilities/application-parameters';
import {
  AuthorizeDirector as AuthorizeDirectorImport,
  AuthorizeDirectorProps,
} from '../../../authorize/AuthorizeDirector';
import { ReportsDirector } from '../../../reports/ReportsDirector';
import { ExpenseCheckDirector } from '../../../expense-check/components/ExpenseCheckDirector';
import { LazyBulkPaymentDirector } from '../../../payments/BulkPayments/components/LazyBulkPaymentDirector';
import { useConfigurationFeatureFlag } from '../../../utilities/useConfigurationFeatureFlag';
import { setApplicationError as setApplicationErrorConnect } from '../../../stores/app-state-store';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { Provider, ProviderEntity } from '../../../models/provider';
import { ERR_PROVIDER_NOT_FOUND, ERR_USER_CANCELLED } from '../../../utilities/error';
import { AppState } from '../../../stores/redux-store';
import { LoadingOverlay } from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { SubmitCredentialsWrapper } from '../../screens/SubmitCredentials/SubmitCredentialsWrapper';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { useVersion } from '../../ApplicationProviders/VersionProvider/VersionProvider';
import { AccountCheckDirector } from '../../../banking/account-check/components/AccountCheckDirector/AccountCheckDirector';
import { InstantPISDirector } from '../../../payments/legacy/components/InstantPISDirector/InstantPISDirector';
import { parentMessenger } from '../../../core/Analytics/ParentApplicationEventMessenger';
import { DeviceHandoffPay } from '../../../payments/legacy/components/screens/DeviceHandoffPay/DeviceHandoffPay';
import { UpdateConsentDirector } from '../../UpdateConsentDirector/UpdateConsentDirector';
import { buildHomeUrl, buildProviderRoute } from '../../../routes';
import {
  ProductDimensionAccountCheck,
  ProductDimensionAuthorize,
  ProductDimensionBusinessAccountCheck,
  ProductDimensionCredentials,
  ProductDimensionExpenseCheck,
  ProductDimensionExtendConsent,
  ProductDimensionIncomeCheck,
  ProductDimensionPay,
  ProductDimensionProducts,
  ProductDimensionReports,
  ProductDimensionRiskInsights,
  ProductDimensionTransactions,
} from '../../../models/product';
import { CredentialsDirector } from '../../CredentialsDirector/CredentialsDirector';
import { ProviderHealthStatusProps } from '../../screens/UnhealthyProvider/UnhealthyProvider';
import {
  ExtendConsentDirector,
  extendConsentProducts,
} from '../../../extend-consent/components/ExtendConsentDirector';
import { IncomeCheckDirector } from '../../../banking/income-check/components/IncomeCheckDirector/IncomeCheckDirector';
import { TransactionsDirector } from '../../../banking/transactions/components/TransactionsDirector/TransactionsDirector';
import { ProductsDirector } from '../../../products/components/ProductsDirector';
import { UnstableProviderWarning } from '../../screens/ProviderTemporaryDisabled/UnstableProviderWarning';
import { BusinessAccountCheckDirector } from '../../../banking/business-account-check/components/BusinessAccountCheckDirector';
import {
  addDemobankCredentialsToProvider as addDemobankCredentialsToProviderImport,
  findProvider,
} from '../../../utilities/provider-utils';
import { RiskInsightsDirector } from '../../../risk-insights/components/RiskInsightsDirector/RiskInsightsDirector';
import { useDemoBankScenarioProvider } from '../../ApplicationProviders/DemoBankScenarioProvider/DemoBankScenarioProvider';
import { LazyDirectPaymentMachineView } from '../../../payments/DirectPayments/LazyDirectPaymentMachineView';
import { LazyFTSMachineView } from '../../../payments/DirectPayments/FTS/LazyFTSMachineView';
import { SelectedProviderContext } from './SelectedProviderContext';

export const providerHealthScreensEnabled = (product: ProductType) => {
  const enabledProducts: ProductType[] = [
    ProductType.PAY,
    ProductType.CREDENTIALS,
    ProductType.ACCOUNT_CHECK,
    ProductType.BUSINESS_ACCOUNT_CHECK,
    ProductType.INCOME_CHECK,
    ProductType.TRANSACTIONS,
    ProductType.BUSINESS_TRANSACTIONS,
    ProductType.REPORTS,
    ProductType.RISK_INSIGHTS,
    ProductType.EXPENSE_CHECK,
  ];

  return enabledProducts.includes(product);
};

type ProviderContainerUrlParams = { providerName: string };

export type ProviderContainerProps = PropsFromRedux & {
  showProviderHealthScreens?: typeof providerHealthScreensEnabled;
  UnhealthyProvider?: React.ComponentType<ProviderHealthStatusProps>;
  AuthorizeDirector?: ComponentType<AuthorizeDirectorProps>;
  addDemobankCredentialsToProvider?: typeof addDemobankCredentialsToProviderImport;
};

export const ProviderContainerPresenter: React.FC<ProviderContainerProps> = ({
  providers,
  setApplicationError,
  showProviderHealthScreens = providerHealthScreensEnabled,
  AuthorizeDirector = AuthorizeDirectorImport,
  addDemobankCredentialsToProvider = addDemobankCredentialsToProviderImport,
}: ProviderContainerProps) => {
  const history = useHistory();
  const { providerName } = useParams<ProviderContainerUrlParams>();
  const [UnstableProviderWarningShown, setUnstableProviderWarningShown] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ProviderEntity | undefined>(undefined);
  const { productType, productDimension } = useProduct();
  const { tinklinkAnalytics } = useAnalytics();
  const {
    version: { version },
  } = useVersion();
  const { selectedDemoBankScenario } = useDemoBankScenarioProvider();

  const hideProviderHealthScreens = useConfigurationFeatureFlag(
    ConfigurationFeatureFlag.HIDE_PROVIDER_HEALTH_SCREENS
  );

  useEffect(() => {
    const [errorMessage, provider] = findProvider(
      providers as ProviderEntity[],
      providerName,
      version
    );

    if (errorMessage || !provider) {
      setApplicationError(ERR_PROVIDER_NOT_FOUND);

      return undefined;
    }

    tinklinkAnalytics.update({ providerName: provider.name });
    parentMessenger.setProvider(provider);

    if (selectedDemoBankScenario) {
      setSelectedProvider(addDemobankCredentialsToProvider(provider, selectedDemoBankScenario));
    } else {
      setSelectedProvider(provider);
    }

    return () => {
      setSelectedProvider(undefined);
    };
  }, [
    setApplicationError,
    tinklinkAnalytics,
    version,
    providers,
    providerName,
    setSelectedProvider,
    selectedDemoBankScenario,
    addDemobankCredentialsToProvider,
  ]);

  if (!selectedProvider || !productType) {
    return <LoadingOverlay />;
  }

  const providerDown = Provider.isDown(selectedProvider);
  const providerDisabled = Provider.isTemporaryDisabled(selectedProvider);
  const providerUnhealthy =
    Provider.isUnhealthy(selectedProvider) &&
    showProviderHealthScreens(productType) &&
    !UnstableProviderWarningShown &&
    !hideProviderHealthScreens;

  if (providerDown || providerDisabled || providerUnhealthy) {
    return (
      <UnstableProviderWarning
        provider={selectedProvider}
        onClose={() => {
          setUnstableProviderWarningShown(true);
        }}
        onUserCancel={() => {
          setApplicationError(ERR_USER_CANCELLED);
        }}
        onBack={() => {
          history.push(buildHomeUrl({ version, productType, productDimension }));
        }}
      />
    );
  }

  return (
    <ConsentDirector>
      <SelectedProviderContext.Provider value={{ selectedProvider }}>
        <Switch>
          <Route
            exact
            path={[
              '/:version/pay/:productDimension/:providerName/handoff-mobile-device',
              '/:version/pay/:productDimension/:providerName/handoff-mobile-device-with-source-account',
            ]}
            component={DeviceHandoffPay}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.AUTHORIZE,
              productDimension: ProductDimensionAuthorize.CONNECT,
            })}
            component={AuthorizeDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.RISK_INSIGHTS,
              productDimension: ProductDimensionRiskInsights.CREATE_REPORT,
            })}
            component={RiskInsightsDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.ACCOUNT_CHECK,
              productDimension: ProductDimensionAccountCheck.CREATE_REPORT,
            })}
            component={AccountCheckDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.BUSINESS_ACCOUNT_CHECK,
              productDimension: ProductDimensionBusinessAccountCheck.CREATE_REPORT,
            })}
            component={BusinessAccountCheckDirector}
          />
          <Route
            exact
            path={[
              buildProviderRoute({
                productType: ProductType.INCOME_CHECK,
                productDimension: ProductDimensionIncomeCheck.CONNECT,
              }),
              buildProviderRoute({
                productType: ProductType.INCOME_CHECK,
                productDimension: ProductDimensionIncomeCheck.CREATE_REPORT,
              }),
            ]}
            component={IncomeCheckDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.EXPENSE_CHECK,
              productDimension: ProductDimensionExpenseCheck.CREATE_REPORT,
            })}
            component={ExpenseCheckDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.REPORTS,
              productDimension: ProductDimensionReports.CREATE_REPORT,
            })}
            component={ReportsDirector}
          />
          <Route
            exact
            path={[
              buildProviderRoute({
                productType: ProductType.TRANSACTIONS,
                productDimension: ProductDimensionTransactions.ADD_ACCOUNTS,
              }),
              buildProviderRoute({
                productType: ProductType.TRANSACTIONS,
                productDimension: ProductDimensionTransactions.CONNECT_ACCOUNTS,
              }),
            ]}
            component={TransactionsDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.BUSINESS_TRANSACTIONS,
              productDimension: ProductDimensionTransactions.CONNECT_ACCOUNTS,
            })}
            component={TransactionsDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.PRODUCTS,
              productDimension: ProductDimensionProducts.CONNECT_ACCOUNTS,
            })}
            component={ProductsDirector}
          />
          <Route
            exact
            path={extendConsentProducts.map((extendConsentProductType) => {
              return buildProviderRoute({
                productType: extendConsentProductType,
                productDimension: ProductDimensionExtendConsent.EXTEND_CONSENT,
              });
            })}
            component={ExtendConsentDirector}
          />
          <Route
            exact
            path="/:version/pay/direct/:providerName/with-source-account"
            component={InstantPISDirector}
          />
          <Route
            exact
            path="/:version/pay/direct/:providerName/fts"
            component={LazyFTSMachineView}
          />
          <Route
            path={buildProviderRoute({
              productType: ProductType.PAY,
              productDimension: ProductDimensionPay.DIRECT,
            })}
            component={LazyDirectPaymentMachineView}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.PAY,
              productDimension: ProductDimensionPay.BULK,
            })}
            component={LazyBulkPaymentDirector}
          />
          <Route
            exact
            path={[
              buildProviderRoute({
                productType: ProductType.TRANSACTIONS,
                productDimension: ProductDimensionTransactions.UPDATE_CONSENT,
              }),
              buildProviderRoute({
                productType: ProductType.PRODUCTS,
                productDimension: ProductDimensionProducts.UPDATE_CONSENT,
              }),
              buildProviderRoute({
                productType: ProductType.ACCOUNT_CHECK,
                productDimension: ProductDimensionAccountCheck.UPDATE_CONSENT,
              }),
            ]}
            component={UpdateConsentDirector}
          />
          <Route
            exact
            path={buildProviderRoute({
              productType: ProductType.CREDENTIALS,
              productDimension: ProductDimensionCredentials.ADD,
            })}
          >
            <CredentialsDirector>
              <SubmitCredentialsWrapper
                onBack={undefined}
                provider={selectedProvider}
                refreshableItems={applicationParameters.get('refreshableItems')}
              />
            </CredentialsDirector>
          </Route>
        </Switch>
      </SelectedProviderContext.Provider>
    </ConsentDirector>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    userId: state.user.user?.id ?? '',
    providers: state.providers.data,
  };
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const ProviderContainer = connector(ProviderContainerPresenter);
